/**
 * Section « Rétention & engagement », suivie des insights produit.
 *
 * ── La cohorte, et pourquoi le filtre de période agit différemment ici ──────
 * Sur les autres onglets, la période est une fenêtre d'OBSERVATION : on regarde
 * ce qui s'est passé entre deux dates. Ici, elle définit une COHORTE : les
 * comptes dont la première action tombe dans cet intervalle, suivis ensuite sur
 * tout l'historique disponible.
 *
 * C'est une différence de nature, pas de détail, et elle est annoncée dans les
 * sous-titres. Calculée autrement, la rétention ne voudrait rien dire : un
 * compte inscrit il y a un an passerait pour nouveau dès qu'il rejoue dans la
 * fenêtre.
 *
 * ── La comparaison abonnés / non-abonnés, derrière un interrupteur ──────────
 * Les trois graphiques BASCULENT en mode comparé, ils ne se doublent pas : la
 * page garde le même nombre de cartes, elle en change la lecture. Un onglet qui
 * afficherait les deux versions côte à côte ferait six graphiques là où on n'en
 * lit qu'un à la fois.
 *
 * L'interrupteur vit dans la query string (`?cmp=1`), à côté de la période et
 * pour les mêmes raisons : il survit au rechargement et l'URL reste partageable.
 *
 * Deux tableaux s'ajoutent en mode comparé : la durée de vie (sur la COHORTE)
 * et l'activité de session (sur tous les comptes actifs de la PÉRIODE —
 * population différente, annoncée comme telle).
 *
 * Elles décrivent un ÉCART, elles ne mesurent pas un EFFET, et l'écart est
 * mécaniquement gonflé : le segment est lu sur l'état d'aujourd'hui alors que
 * la rétention part de la première action, si bien que la rétention antérieure
 * à l'abonnement est portée au crédit des abonnés. Le délai médian avant
 * abonnement est affiché pour ça — c'est la clé de lecture, pas une précision.
 */

import { AlertList } from '../components/AlertList';
import { CustomizerTable } from '../components/CustomizerTable';
import { EngagementHistogram } from '../components/EngagementHistogram';
import { RetentionChart } from '../components/RetentionChart';
import { SectionShell } from '../components/SectionShell';
import { SegmentComparisonTables } from '../components/SegmentComparisonTable';
import { SegmentHistogram } from '../components/SegmentHistogram';
import { SegmentRetentionChart } from '../components/SegmentRetentionChart';
import { ToggleSwitch } from '../components/ToggleSwitch';
import { StatTile } from '../components/StatTile';
import { useAuth } from '../hooks/useAuth';
import { useComparePref } from '../hooks/useComparePref';
import { useEngagementStats } from '../hooks/usePeriodStats';
import { usePeriodParam } from '../hooks/usePeriodParam';
import { formatDecimal, formatInteger, formatPercent } from '../lib/format';

export function EngagementPage() {
  const { token, signOut } = useAuth();
  const [period] = usePeriodParam();
  const [compare, setCompare] = useComparePref();

  const { data, error, isPending, isFetching } = useEngagementStats({
    from: period.from,
    to: period.to,
    token,
    onUnauthorized: signOut,
  });

  const lifetime = data?.lifetime;
  const d7 = data?.retention.find((point) => point.day === 7);
  const d30 = data?.retention.find((point) => point.day === 30);

  return (
    <SectionShell
      lastSyncAt={data?.meta.lastSyncAt}
      isPending={isPending}
      isFetching={isFetching}
      error={error}
      hasData={Boolean(data)}
      beforeContent={
        <ToggleSwitch
          label="Comparer abonnés et non-abonnés"
          hint="Bascule les trois graphiques et ajoute deux tableaux — le segment est « s'est abonné au moins une fois »"
          checked={compare}
          onChange={setCompare}
        />
      }
    >
      <div className="kpi-row">
        <StatTile
          label="Cohorte"
          value={formatInteger(lifetime?.cohortUsers ?? 0)}
          hint="comptes dont la 1re action est dans la période"
        />
        <StatTile
          label="Rétention J+7"
          value={d7 ? formatPercent(d7.ratePercent) : '—'}
          hint={d7 ? `sur ${formatInteger(d7.eligibleUsers)} comptes éligibles` : ''}
        />
        <StatTile
          label="Rétention J+30"
          value={d30 ? formatPercent(d30.ratePercent) : '—'}
          hint={d30 ? `sur ${formatInteger(d30.eligibleUsers)} comptes éligibles` : ''}
        />
        <StatTile
          label="Actifs un seul jour"
          value={formatPercent(lifetime?.singleDayShare ?? 0)}
          hint="le signal d'onboarding"
        />
        <StatTile
          label="Jours d'activité"
          value={formatDecimal(lifetime?.medianActiveDays ?? 0)}
          hint={`médiane — moyenne ${formatDecimal(lifetime?.avgActiveDays ?? 0)}`}
        />
      </div>

      <div className="chart-grid">
        {/* Les trois graphiques BASCULENT, ils ne se doublent pas : la page
            garde le meme nombre de cartes, elle en change la lecture. */}
        {compare && data ? (
          <>
            <SegmentRetentionChart data={data.subscriberComparison} />

            <SegmentHistogram
              data={data.subscriberComparison.lifetimeHistogram}
              title="Durée de vie comparée"
              subtitle="Jours entre la première et la dernière action — part dans chaque segment"
            />
            <SegmentHistogram
              data={data.subscriberComparison.activeDaysHistogram}
              title="Jours d'activité comparés"
              subtitle="Jours distincts où le compte a agi — part dans chaque segment"
            />

            <SegmentComparisonTables data={data.subscriberComparison} />
          </>
        ) : (
          <>
            <div className="chart-grid__wide">
              <RetentionChart data={data?.retention ?? []} />
            </div>

            <EngagementHistogram
              data={data?.lifetimeHistogram ?? []}
              title="Durée de vie des comptes"
              subtitle="Jours entre la première et la dernière action"
            />
            <EngagementHistogram
              data={data?.activeDaysHistogram ?? []}
              title="Jours d'activité par compte"
              subtitle="Jours distincts où le compte a agi — sans rapport avec la durée de vie"
            />
          </>
        )}

        <CustomizerTable
          data={
            data?.customizers ?? {
              customizers: 0,
              nonCustomizers: 0,
              customizerAvgSessionSeconds: 0,
              nonCustomizerAvgSessionSeconds: 0,
              customizerAvgActionsPerSession: 0,
              nonCustomizerAvgActionsPerSession: 0,
            }
          }
        />

        <AlertList alerts={data?.alerts ?? []} />
      </div>

      <p className="footnote">
        Ici la période ne sert pas de fenêtre d'observation mais définit une cohorte : les
        comptes dont la <strong>première action</strong> tombe dans l'intervalle, suivis ensuite
        sur tout l'historique. Un compte n'est compté au dénominateur d'un jalon que s'il a eu le
        temps de revenir — sans cette règle, chaque vague d'inscriptions ferait plonger le taux
        mécaniquement. La durée de vie d'un compte actif un seul jour vaut 0, même convention que
        pour les sessions. Comptes de test (1 à 4) exclus.
      </p>

      {compare && (
      <p className="footnote">
        <strong>La comparaison abonnés / non-abonnés décrit un écart, pas un effet.</strong> Le
        segment est « s'est abonné au moins une fois » et non l'état courant — sinon un compte
        abonné huit mois puis résilié se retrouverait chez les « jamais abonnés » avec toute sa
        rétention. Deux biais restent, et le second est le plus vicieux : les gens assidus
        s'abonnent (s'abonner ne rend pas assidu), et surtout le segment est lu sur l'état
        d'<strong>aujourd'hui</strong> alors que la rétention part de la première action — la
        rétention <em>antérieure</em> à l'abonnement est donc portée au crédit des abonnés. Le
        délai médian avant abonnement, sous la courbe, dit quelle part de l'écart est
        tautologique. Un taux n'est publié qu'à partir de 20 comptes éligibles dans le segment :
        la découpe divisant les effectifs, le trait s'interrompt plus souvent que sur la courbe
        globale. Les comptes supprimés côté prod sont écartés des deux segments plutôt que versés
        chez les non-abonnés, et leur nombre est annoncé. Les histogrammes comparent des{' '}
        <strong>parts dans chaque segment</strong> et non des effectifs : les deux groupes n'ont
        pas la même taille, et des barres brutes ne diraient que ça.
      </p>
      )}
    </SectionShell>
  );
}
